// User access management: who can sign in, what role they hold on which
// project, and what each role is allowed to do.
//
// Two tabs because they answer different questions. "Users" is the day-to-day
// job of onboarding someone; "Roles & Permissions" is the occasional job of
// deciding what a role means. Splitting them keeps the common task simple.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, ShieldCheck, Users } from 'lucide-react';
import { accessApi, type AccessUser, type Permission, type Role } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import UsersTab from './UsersTab';
import RolesTab from './RolesTab';

type Project = { id: string; name: string };
type Tab = 'users' | 'roles';

export default function AccessManagementPage({ projects }: { projects: Project[] }) {
    const { refresh: refreshMyAccess } = useAuth();
    const [tab, setTab] = useState<Tab>('users');
    const [users, setUsers] = useState<AccessUser[]>([]);
    const [roles, setRoles] = useState<Role[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const load = useCallback(async () => {
        setError(null);
        try {
            const [u, r, p] = await Promise.all([
                accessApi.listUsers(),
                accessApi.listRoles(),
                accessApi.listPermissions(),
            ]);
            setUsers(u);
            setRoles(r);
            setPermissions(p);
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to load access data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Editing roles can change what the current user may do, so refresh the
    // session's own permissions too — otherwise the nav and buttons keep
    // reflecting stale access until a manual reload.
    const reloadAll = useCallback(async () => {
        await load();
        await refreshMyAccess();
    }, [load, refreshMyAccess]);

    const permissionsByCategory = useMemo(() => {
        const groups = new Map<string, Permission[]>();
        for (const p of permissions) {
            if (!groups.has(p.category)) groups.set(p.category, []);
            groups.get(p.category)!.push(p);
        }
        return [...groups.entries()];
    }, [permissions]);

    const TABS: { value: Tab; label: string; Icon: typeof Users; count: number }[] = [
        { value: 'users', label: 'Users', Icon: Users, count: users.filter((u) => u.has_login).length },
        { value: 'roles', label: 'Roles & Permissions', Icon: ShieldCheck, count: roles.length },
    ];

    return (
        <div className="h-full flex flex-col bg-gray-50 dark:bg-slate-900 overflow-hidden">
            <header className="flex-shrink-0 px-6 pt-5 pb-0 bg-white dark:bg-slate-800 border-b border-gray-200 dark:border-slate-700">
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">User Access</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                    Manage sign-in accounts, role assignments and what each role can do.
                </p>

                <nav className="flex gap-1 mt-4" role="tablist">
                    {TABS.map(({ value, label, Icon, count }) => {
                        const active = tab === value;
                        return (
                            <button
                                key={value}
                                role="tab"
                                aria-selected={active}
                                onClick={() => setTab(value)}
                                className={[
                                    'inline-flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-px transition-colors',
                                    active
                                        ? 'border-indigo-600 text-indigo-600 dark:text-indigo-400 dark:border-indigo-400'
                                        : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200',
                                ].join(' ')}
                            >
                                <Icon size={15} />
                                {label}
                                <span className="text-xs text-gray-400 dark:text-gray-500">{count}</span>
                            </button>
                        );
                    })}
                </nav>
            </header>

            <div className="flex-1 min-h-0 overflow-auto p-6">
                {loading ? (
                    <div className="flex items-center justify-center py-20">
                        <Loader2 size={22} className="animate-spin text-indigo-600" />
                    </div>
                ) : error ? (
                    <div className="max-w-lg mx-auto mt-10 rounded-lg bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 p-4">
                        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
                        <button
                            onClick={load}
                            className="mt-3 text-sm font-medium text-red-700 dark:text-red-300 underline"
                        >
                            Try again
                        </button>
                    </div>
                ) : tab === 'users' ? (
                    <UsersTab
                        users={users}
                        roles={roles}
                        projects={projects}
                        onChanged={reloadAll}
                    />
                ) : (
                    <RolesTab
                        roles={roles}
                        permissionsByCategory={permissionsByCategory}
                        onChanged={reloadAll}
                    />
                )}
            </div>
        </div>
    );
}
